import React from "react"
import { format, addDays } from "date-fns"
import PropTypes from "prop-types"

// Days of the week for the schedule (Mon-Sat)
const WEEK_DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"]

/**
 * Format a number as currency (dollars)
 * @param {number} value - The value to format
 * @param {boolean} includeSymbol - Whether to include the $ symbol
 * @returns {string} Formatted currency string
 */
function formatCurrency(value, includeSymbol = true) {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return includeSymbol ? "$0" : "0"
  }
  const formatted = Math.round(value).toLocaleString()
  return includeSymbol ? `$${formatted}` : formatted
}

/**
 * Format hours for display
 * @param {number} value - The hours value
 * @param {boolean} includeSuffix - Whether to include the 'h' suffix
 * @returns {string} Formatted hours string
 */
function formatHours(value, includeSuffix = true) {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return includeSuffix ? "0h" : "0"
  }
  const formatted = value.toFixed(1)
  return includeSuffix ? `${formatted}h` : formatted
}

/**
 * Determine the performance status based on actual vs target
 * @param {number} actual - Actual value
 * @param {number} target - Target value
 * @returns {string} "above", "below", or "neutral"
 */
function getPerformanceStatus(actual, target) {
  if (!target || target === 0) {
    return "neutral"
  }
  const percentage = (actual / target) * 100
  if (percentage >= 100) {
    return "above"
  }
  if (percentage >= 80) {
    return "neutral"
  }
  return "below"
}

/**
 * TargetMetricsRow component displays the target hours and dollars,
 * daily actual hours and dollars, and weekly totals for a surveyor.
 * It applies color coding based on performance (green for meeting target, red for below).
 *
 * This component integrates with existing KPI calculation services:
 * - SurveyorGoalsService for hours targets and actuals
 * - TargetCalculationService for dollar targets and actuals
 *
 * @param {Object} props
 * @param {Object} props.metrics - Metrics data from the API
 * @param {number} props.metrics.target_hours - Weekly target hours
 * @param {number} props.metrics.target_dollars - Weekly target dollars
 * @param {Object} props.metrics.daily_actuals - Daily actual values keyed by date
 * @param {Object} props.metrics.weekly_totals - Weekly total hours and dollars
 * @param {Date} props.weekStartDate - Start date of the week (Monday)
 * @param {Object} props.workingSchedule - Surveyor's working schedule
 */
export default function TargetMetricsRow({
  metrics, scheduleData, weekStartDate, workingSchedule
}) {
  if (!metrics) {
    return null
  }

  const {
    target_hours: targetHours = 0,
    target_dollars: targetDollars = 0,
    daily_actuals: dailyActuals = {},
    weekly_totals: weeklyTotals = {}
  } = metrics

  const weeklyActualHours = weeklyTotals.hours || 0
  const weeklyActualDollars = weeklyTotals.dollars || 0

  // Determine overall performance status
  const hoursStatus = getPerformanceStatus(weeklyActualHours, targetHours)
  const dollarsStatus = getPerformanceStatus(weeklyActualDollars, targetDollars)

  // Compute per-day and weekly est. hours + quoted price from job_services
  const days = scheduleData?.days || {}
  const dailyEstHours = {}
  const dailyQuotedPrice = {}
  let weeklyEstHours = 0
  let weeklyQuotedPrice = 0

  WEEK_DAYS.forEach((day, dayIndex) => {
    const dayDate = addDays(weekStartDate, dayIndex)
    const dateKey = format(dayDate, "yyyy-MM-dd")
    const jobServices = days[dateKey]?.job_services || []

    const estHours = jobServices.reduce((sum, js) => sum + (parseFloat(js.estimated_hours) || 0), 0)
    const quoted = jobServices.reduce((sum, js) => sum + (parseFloat(js.total_cost || js.price) || 0), 0)

    dailyEstHours[dateKey] = estHours
    dailyQuotedPrice[dateKey] = quoted
    weeklyEstHours += estHours
    weeklyQuotedPrice += quoted
  })

  return (
    <tr className="target-metrics-row">
      {/* Surveyor name column - shows est/quoted totals then targets */}
      <td className="surveyor-name-col metrics-targets-cell">
        <div className="metrics-targets">
          <div className="target-item">
            <span className="target-label">Est. Hours:</span>
            <span className="target-value">{formatHours(weeklyEstHours)}</span>
          </div>
          <div className="target-item">
            <span className="target-label">Hours:</span>
            <span className="target-value">{formatHours(targetHours)}</span>
          </div>
          <div className="target-item">
            <span className="target-label">Quoted:</span>
            <span className="target-value">{formatCurrency(weeklyQuotedPrice)}</span>
          </div>
          <div className="target-item">
            <span className="target-label">Value:</span>
            <span className="target-value">{formatCurrency(targetDollars)}</span>
          </div>
        </div>
      </td>

      {/* Daily columns */}
      {WEEK_DAYS.map((day, dayIndex) => {
        const dayDate = addDays(weekStartDate, dayIndex)
        const dateKey = format(dayDate, "yyyy-MM-dd")
        const dayActuals = dailyActuals[dateKey] || { hours: 0, dollars: 0 }

        const dayKey = day.toLowerCase()
        const daySchedule = workingSchedule?.[dayKey]
        const isWorking = daySchedule?.works

        if (!isWorking) {
          return (
            <td key={day} className="day-col metrics-cell not-working-metrics">
              <span className="not-working-text">-</span>
            </td>
          )
        }

        return (
          <td key={day} className="day-col metrics-cell">
            <div className="daily-metrics">
              <div className="metric-value est-hours-value">
                {formatHours(dailyEstHours[dateKey] || 0)}
              </div>
              <div className="metric-value hours-value">
                {formatHours(dayActuals.hours)}
              </div>
              <div className="metric-value quoted-value">
                {formatCurrency(dailyQuotedPrice[dateKey] || 0)}
              </div>
              <div className="metric-value dollars-value">
                {formatCurrency(dayActuals.dollars)}
              </div>
            </div>
          </td>
        )
      })}

      {/* Total column - unchanged */}
      <td className="total-col metrics-totals-cell">
        <div className="weekly-totals">
          <div className={`total-item hours-total ${hoursStatus}`}>
            <span className="total-value">
              {`${formatHours(weeklyActualHours, false)} / ${formatHours(targetHours)}`}
            </span>
          </div>
          <div className={`total-item dollars-total ${dollarsStatus}`}>
            <span className="total-value">{formatCurrency(weeklyActualDollars)}</span>
          </div>
        </div>
      </td>
    </tr>
  )
}

TargetMetricsRow.propTypes = {
  metrics: PropTypes.shape({
    target_hours: PropTypes.number,
    target_dollars: PropTypes.number,
    daily_actuals: PropTypes.objectOf(
      PropTypes.shape({
        hours: PropTypes.number,
        dollars: PropTypes.number
      })
    ),
    weekly_totals: PropTypes.shape({
      hours: PropTypes.number,
      dollars: PropTypes.number
    })
  }),
  scheduleData: PropTypes.shape({
    days: PropTypes.objectOf(PropTypes.shape({
      job_services: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number }))
    }))
  }),
  weekStartDate: PropTypes.instanceOf(Date).isRequired,
  workingSchedule: PropTypes.objectOf(
    PropTypes.shape({
      works: PropTypes.bool,
      start_time: PropTypes.string,
      end_time: PropTypes.string
    })
  )
}

TargetMetricsRow.defaultProps = {
  metrics: null,
  scheduleData: null,
  workingSchedule: {}
}
